<template>
  <ControlWrapper
    v-bind="control.controlWrapper.value"
    :styles="control.styles"
    :is-focused="control.isFocused.value"
    :applied-options="control.appliedOptions.value"
    :error="control.control.value.errors"
  >
    <h3 :class="control.styles.control.label">{{ control.computedLabel }}</h3>
    <div class="widget-select-button">
      <WButton
        v-for="(label, index) in labels"
        :key="String(label)"
        :disabled="!control.control.value.enabled"
        :autofocus="control.appliedOptions.value.focus"
        :data-selected="value === values[index]"
        type="outline"
        :variant="getClientTheme()"
        @click="valueChanged(values[index])"
        >{{ label }}</WButton
      >
    </div>
    <HeightTransition :visible="value === false && !isInHours">
      <div class="pt-8">
        <ActionCard
          :title="schema.outOfHours?.title"
          :icon="schema.outOfHours?.icon"
          :content="schema.outOfHours?.description"
        >
          <template #action>
            <ContactInfoCard
              v-if="schema.outOfHours?.action.type === 'call'"
              :phone="{
                number: outOfHoursPhone,
                title: schema.outOfHours?.action.label,
              }"
            />
          </template>
        </ActionCard>
      </div>
    </HeightTransition>
  </ControlWrapper>
</template>

<script setup lang="ts">
  import WButton from '../../base/Button.vue';
  import { computed, ref } from 'vue';
  import { ControlElement } from '@jsonforms/core';

  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import ControlWrapper from './ControlWrapper.vue';
  import { useWidgetControl } from '../util';
  import { config, getClientTheme } from 'src/utils/config';

  import HeightTransition from 'src/components/base/HeightTransition.vue';
  import ActionCard from 'src/components/base/ActionCard.vue';
  import { isOpenHours } from 'src/utils/helpers';
  import ContactInfoCard from 'src/components/claims-widget/components/ContactInfoCard.vue';

  export type OptionValue = string | number | boolean;

  export interface Option {
    title: string;
    value: OptionValue;
  }

  export interface Action {
    type: string;
    label: string;
    details: string;
    icon: string;
  }

  export interface OutOfHour {
    icon: string;
    title: string;
    description: string;
    action: Action;
  }

  export interface Schema {
    options: Array<Option | OptionValue>;
    title: string;
    type: string;
    outOfHours: OutOfHour;
  }

  const props = defineProps(rendererProps<ControlElement>());
  const control = useWidgetControl(useJsonFormsControl(props));
  const value = ref(control.control.value.data);
  if (
    !(
      'options' in control.control.value.schema &&
      typeof control.control.value.schema.options === 'object' &&
      Array.isArray(control.control.value.schema.options)
    )
  ) {
    throw new Error('must provide options param to option control');
  }
  const schema = ref(control.control.value.schema as Schema);
  const options = schema.value.options;

  const isInHours = computed(() => {
    const selectedIntegration =
      config.value.integrations?.find(
        ({ name, settings }) => name === 'brokerDirect' || settings?.businessHours?.length
      )?.settings || config.value.settings;
    return isOpenHours(selectedIntegration?.businessHours, selectedIntegration?.timezone);
  });
  const outOfHoursPhone = computed(
    () => config.value?.settings?.outOfHoursPhone || schema.value.outOfHours.action.details
  );
  const labels = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.title;
      }
      return option;
    })
  );
  const values = computed(() =>
    options.map((option) => {
      if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
        return option.value;
      }
      return option;
    })
  );

  function valueChanged(val: string | number | boolean | { title: string; value: string | number | boolean }) {
    control.onChange(val);
    value.value = val;
  }
</script>

<style scoped></style>
